<template>
  <ion-page id="gamehistory-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>

      <div v-show="showState == SHOW_HISTORY">
        <TopLine :style="styleTopline" :title="gameHistoryTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>
        <ion-list id="game_history" :style="styleMiddle" lines="full">
          <ion-item v-if="isLoading">
            <ion-label>
              <h1>{{ loadingGameHistory }}</h1>
            </ion-label>
          </ion-item>

          <ion-item>
            <ion-label color="danger" >
              {{ gameHistoryInfo }}
            </ion-label>
          </ion-item>
          <ion-item v-for="item in historyItems" v-bind:key="item.index" @click="onClick(item.index)" lines="full">
            <ion-label class="ion-text-wrap">
                "{{ gameString }} {{ item.index+1 }}" {{ item.date }} 
                <span style="color:red;">{{ item.color }}/{{ item.blindness }} {{ item.moves }} {{ movesString }}</span> {{ item.engine }}({{ item.skill }}) {{ resultString }}={{ item.score }}
            </ion-label>
          </ion-item>
        </ion-list>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
     </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';
  import { computed } from "@vue/reactivity"

  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'

  import ExplainTour from '@/components/ExplainTour.vue'

  import { ref, watch } from 'vue'
  import ToolBar from '@/components/ToolBar.vue'
  import TopLine from '@/components/TopLine.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { useGetData, generateErrorAlertText } from '../../composables/getData.js'
  import { parseWordsSaid , buildWordListFromChoiceList, parseNumberWords } from '@/composables/wordlistSupport.js'
  import { useStringStore } from '@/stores/stringStore';
  import { translateEnglishWords , translatePhrase} from '@/composables/translation.js';

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const windowSizeStore = useWindowSizeStore()

  const connectionStore = useConnectionStore()  
  const isDark = ref(connectionStore.getDarkmode != "light")

  const stringStore = useStringStore()

  const wordlistTrigger = ref(0)
  const wordlistHistory = "command scroll up down game explain done ignore mute listen one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen twenty thirty forty fifty sixty seventy eighty ninety";
  const wordlist = ref(wordlistHistory)
  emit("word-list",wordlistHistory)

  const gameString = ref(translateEnglishWords("Game"))
  const resultString = ref(translateEnglishWords("Result"))
  const movesString = ref(translateEnglishWords("Moves"))
  const loadingGameHistory = ref(translatePhrase("Loading Game History"))
  const gameHistoryInfo = ref(translateEnglishWords("To Review Game : Say ' command game # ' as in ' command game 5 '"))
  const gameHistoryTitle = ref(translateEnglishWords("Game History"))
  const blindnessStrings = ["",translatePhrase('Not Blind'),
                      translatePhrase('Shared Pieces'),
                      translatePhrase('Two Discs'),
                      translatePhrase('Shared Disc'),
                      translatePhrase('Empty Board')
      ]
  const colorStrings = [ translateEnglishWords("Black"), translateEnglishWords("White")]

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  const isLoading = ref(true)
  const SHOW_HISTORY = 0
  const SHOW_EXPLAINTOUR = 1
  const showState = ref(SHOW_HISTORY)
  var explainForState = SHOW_HISTORY
  var explainForStateWordList = ""

  const TOOLBAR_EXPLAINTOUR = 0

  const toolbarElements = ref([
    { index : TOOLBAR_EXPLAINTOUR, label : translateEnglishWords("Explain") ,disabled : false},
  ])

  const toolbarTrigger = ref(0)

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  var scrollTop = 0

  var openGameId = 0

  const historyItems = ref([])

   watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })
   
   watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    getHistory()
  }

  function explainTourInitialize(errorText) {
    console.log("explaintour for -> ",errorText)
    // save which page called for an explanation
    explainForState = showState.value
    explainForStateWordList = wordlist.value

    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? "game_history" : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)  
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {

      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_HISTORY
        wordlist.value = wordlistHistory
        emit("word-list",wordlist.value)
        wordlistTrigger.value++
        break
    }
  }
  
  function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  function toolbarClick(arrayIndex) {
    switch (arrayIndex) {
      case TOOLBAR_EXPLAINTOUR :
        processWords("command explain")
        break

    }
  }

  function onClick(index) {
    const gameId = historyItems.value[index].gameId
    connectionStore.setReviewGameId(gameId + ",/gamehistory")
    emit("where-next","/gamereview")
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain" || currentWord == "mute" || currentWord == "listen") {
            return [currentWord,""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
        if (currentWord == "game") {
          const gameIndex = parseNumberWords(nextWord,nextNextWord)
          return ["game", gameIndex]
        }
       
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function processWords(text) {
    //console.log("text in engine Menu ",text)
    var returnArray = parseWordsSaid(text,parseCommands)
    //console.log("command found",returnArray)

    if (showState.value == SHOW_EXPLAINTOUR) {
      //console.log("*** words for explain tour",text)
      explainTourWordsSpoken.value = text
      explainTourWordsSpokenTrigger.value++
      return;
    }
    switch (returnArray[0]) {
      case "scroll" :
        scrollElement("game_history",returnArray[1])
        break
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "game" :
        const gameIndex = returnArray[1] - 1
        if (gameIndex < 0)
          break
        if (gameIndex > historyItems.value.length -1)
          break
        onClick(gameIndex)
        break
      case "explain" :
        explainTourInitialize(null)
        break
      case "done" :
        emit("where-next","/enginemenu")
        break
    }
  }

  function processJsonResponse (json) {
    //console.log(" in game review callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(generateErrorAlertText(json))
      return;
    }

    var gameCount = Math.floor(json["game_count"])

    const gameIds = json["id"].split("\t")
    const blindnessString = json["blindness"]
    const blindnessExpanded = stringStore.expandServerString(blindnessString)
    const blindnesses = blindnessExpanded.split("\t")
    const moves = json["moves"].split("\t")
    const engines = json["engine"].split("\t")
    const skills = json["skills"].split("\t")
    const dates = json["date"].split("\t")
    const reveals = json["reveal"].split("\t")

    const colorString = json["color"]
    const colorExpanded = stringStore.expandServerString(colorString)
    const colors = colorExpanded.split("\t")
    const scores = json["score"].split("\t")

    if (gameCount > 99)
      gameCount = 99;

    for (var i=0; i < gameCount; i++) {
      const oneGame = { index : i,
              gameId : gameIds[i],
              blindness : (blindnesses[i] == "") ? blindnessStrings[0] : blindnessStrings[blindnesses[i]],
              moves : moves[i],
              engine : engines[i],
              skill : skills[i],
              date : dates[i],
              reveal : reveals[i],
              color : colorStrings[colors[i]],
              score : scores[i]  
            }
      historyItems.value.push(oneGame)
    }

    isLoading.value = false
  }
  
  function getHistory() {

    const timeNow = Math.floor(Date.now() / 1000)

    var formData = {
      time : timeNow
    }

    useGetData("chessvis_engine_history.php", formData, processJsonResponse)
  }

</script>

<style scoped>

.review-note {
  margin-bottom : -20px;
  margin-top : -20px;
}
</style>